import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApi } from "../useApi";
import { useProject } from "../projectContext";
import type { Task } from "../types";

// Tasks page — fetches every task for the project (already aggregated by
// the bedrock-logs Lambda) and renders epics with collapsible descendant
// blocks. Tasks assigned to a human are highlighted.

interface TaskNode {
  task: Task;
  children: TaskNode[];
  // Roll-up of token usage including descendants.
  rolledInputTokens: number;
  rolledOutputTokens: number;
  rolledCostUSD: number;
}

export default function TasksPage() {
  const { project } = useProject();
  const { call } = useApi();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<Task[] | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const [refreshKey, setRefreshKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    setTasks(undefined);
    let cancelled = false;
    call<Task[]>(`/api/projects/${project.projectId}/tasks`)
      .then((t) => { if (!cancelled) { setTasks(t); setRefreshing(false); } })
      .catch((e: Error) => { if (!cancelled) { setError(e.message); setRefreshing(false); } });
    return () => { cancelled = true; };
  }, [project.projectId, call, refreshKey]);

  const tree = useMemo(() => buildTree(tasks ?? []), [tasks]);

  if (error) return <p className="error">{error}</p>;
  if (!tasks) return <p>Loading tasks…</p>;

  return (
    <>
      <div className="page-header">
        <h1>Tasks</h1>
        <p>All Jira issues tracked for {project.name}.</p>
        <button
          className="secondary"
          disabled={refreshing}
          onClick={() => { setRefreshing(true); setError(undefined); setRefreshKey((k) => k + 1); }}
        >
          {refreshing ? "Refreshing…" : "Refresh"}
        </button>
      </div>
      <div className="card" style={{ padding: 0 }}>
        <table>
          <thead>
            <tr>
              <th style={{ width: 40 }}></th>
              <th>Issue</th>
              <th>Summary</th>
              <th>Status</th>
              <th>Assignee</th>
              <th style={{ textAlign: "right" }}>Input Tokens</th>
              <th style={{ textAlign: "right" }}>Output Tokens</th>
              <th style={{ textAlign: "right" }}>Cost (USD)</th>
            </tr>
          </thead>
          <tbody>
            {tree.length === 0 && (
              <tr>
                <td colSpan={8} className="muted" style={{ padding: 24, textAlign: "center" }}>
                  No tasks yet for this project.
                </td>
              </tr>
            )}
            {tree.map((node) => (
              <TaskRow
                key={node.task.jiraIssueKey}
                node={node}
                depth={0}
                project={project}
                expanded={expanded}
                toggle={(key) => {
                  setExpanded((prev) => {
                    const next = new Set(prev);
                    if (next.has(key)) next.delete(key);
                    else next.add(key);
                    return next;
                  });
                }}
                onOpen={(key) =>
                  navigate(`/projects/${project.projectId}/tasks/${encodeURIComponent(key)}`)
                }
              />
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

interface RowProps {
  node: TaskNode;
  depth: number;
  project: { jiraBaseUrl: string; projectId: string };
  expanded: Set<string>;
  toggle: (key: string) => void;
  onOpen: (key: string) => void;
}

function TaskRow({ node, depth, project, expanded, toggle, onOpen }: RowProps) {
  const isOpen = expanded.has(node.task.jiraIssueKey);
  const hasChildren = node.children.length > 0;
  return (
    <>
      <tr
        className={`task-row${node.task.assigneeIsHuman ? " human-assigned" : ""}`}
        style={{ cursor: "pointer" }}
        onClick={() => onOpen(node.task.jiraIssueKey)}
      >
        <td style={{ paddingLeft: 10 + depth * 18 }}>
          {hasChildren ? (
            <button
              className="toggle"
              onClick={(e) => {
                e.stopPropagation();
                toggle(node.task.jiraIssueKey);
              }}
            >
              {isOpen ? "▾" : "▸"}
            </button>
          ) : null}
        </td>
        <td>
          <a
            href={`${project.jiraBaseUrl.replace(/\/$/, "")}/browse/${node.task.jiraIssueKey}`}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            {node.task.jiraIssueKey}
          </a>
        </td>
        <td>{node.task.summary ?? <span className="muted">(no summary)</span>}</td>
        <td>{node.task.status ?? <span className="muted">—</span>}</td>
        <td>
          {node.task.assigneeIsHuman ? (
            <span className="status-badge">Human</span>
          ) : (
            node.task.assigneeAgent ?? <span className="muted">Unassigned</span>
          )}
          {node.task.currentExecutionStatus && node.task.currentExecutionStatus !== "NONE" && (
            <span className={`status-badge ${node.task.currentExecutionStatus}`} style={{ marginLeft: 6 }}>
              {node.task.currentExecutionStatus}
            </span>
          )}
        </td>
        <td style={{ textAlign: "right" }}>
          {node.task.totalInputTokens.toLocaleString()}
          {hasChildren && <span className="muted"> ({node.rolledInputTokens.toLocaleString()})</span>}
        </td>
        <td style={{ textAlign: "right" }}>
          {node.task.totalOutputTokens.toLocaleString()}
          {hasChildren && <span className="muted"> ({node.rolledOutputTokens.toLocaleString()})</span>}
        </td>
        <td style={{ textAlign: "right" }}>{node.rolledCostUSD.toFixed(2)}</td>
      </tr>
      {isOpen &&
        node.children.map((child) => (
          <TaskRow
            key={child.task.jiraIssueKey}
            node={child}
            depth={depth + 1}
            project={project}
            expanded={expanded}
            toggle={toggle}
            onOpen={onOpen}
          />
        ))}
    </>
  );
}

// Builds a tree of epic → descendants, only showing epics at the top level.
// Tasks without a known epic are surfaced as roots.
function buildTree(tasks: Task[]): TaskNode[] {
  const byKey = new Map<string, TaskNode>();
  for (const task of tasks) {
    byKey.set(task.jiraIssueKey, {
      task,
      children: [],
      rolledInputTokens: task.totalInputTokens ?? 0,
      rolledOutputTokens: task.totalOutputTokens ?? 0,
      rolledCostUSD: task.totalCostUSD ?? 0,
    });
  }

  const roots: TaskNode[] = [];
  for (const node of byKey.values()) {
    const parentKey = node.task.parentIssueKey;
    if (parentKey && byKey.has(parentKey) && parentKey !== node.task.jiraIssueKey) {
      byKey.get(parentKey)!.children.push(node);
    } else {
      roots.push(node);
    }
  }

  // Roll up token / cost totals from descendants onto ancestors.
  const rollUp = (node: TaskNode): void => {
    for (const child of node.children) {
      rollUp(child);
      node.rolledInputTokens += child.rolledInputTokens;
      node.rolledOutputTokens += child.rolledOutputTokens;
      node.rolledCostUSD += child.rolledCostUSD;
    }
    node.children.sort((a, b) => a.task.jiraIssueKey.localeCompare(b.task.jiraIssueKey));
  };
  roots.forEach(rollUp);
  roots.sort((a, b) => a.task.jiraIssueKey.localeCompare(b.task.jiraIssueKey));
  return roots;
}
